import { useCallback } from 'react'
import { useMutation, type ReactMutation } from 'convex/react'
import type { FunctionReference } from 'convex/server'
import { create } from 'zustand'

/**
 * Saving, with somewhere for failures to go.
 *
 * Almost every field in this app saves on blur and nothing waits for the
 * result — which is right, because waiting would make typing feel like
 * submitting a form. But it left a hole: when the server refused a write, the
 * field silently snapped back to its old value and the user was told nothing.
 * A rejection you cannot see is indistinguishable from the app losing your
 * work, and it is the fastest way to stop trusting an app.
 *
 * So every mutation goes through here, and a failure lands in one place the
 * shell can show. Reported rather than thrown: the caller said `void` because
 * it genuinely has nothing to do about it, and turning that into an unhandled
 * rejection helps nobody.
 *
 * The exception is quick capture, which deliberately uses Convex's hook
 * directly — it puts the text back in the field, which is more useful than
 * a notice, and doing both would say the same thing twice.
 */

type SaveState = {
  /** The last failure, or null. Shown by `SaveNotice`. */
  message: string | null
  report: (error: unknown) => void
  dismiss: () => void
}

export const useSaveState = create<SaveState>((set) => ({
  message: null,
  report: (error) => set({ message: readableMessage(error) }),
  dismiss: () => set({ message: null }),
}))

/**
 * The sentence written for a person, dug out of whatever the server threw.
 *
 * `ConvexError` first, and it is the reason the server throws that rather than
 * `Error`: Convex deliberately redacts ordinary exception messages in
 * production, so "Times per week must be between 1 and 7" arrived in a
 * development build and arrived as "Server Error" in the deployed one. Every
 * validation message in the app therefore read as "That did not save. Check
 * your connection" to anyone actually using it — a wrong number reported as a
 * network fault, with the value left in the field.
 *
 * The regex below still handles a plain `Error` from a development build,
 * which is framed as `[CONVEX M(items:updateSchedule)] [Request ID: …] Server
 * Error Uncaught Error: <message>`.
 */
export function readableMessage(error: unknown): string {
  const data = (error as { data?: unknown } | null)?.data
  if (typeof data === 'string' && data.trim().length > 0) return data.trim()

  const raw = error instanceof Error ? error.message : String(error)
  const match = /Uncaught (?:Convex)?Error:\s*([^\n]+)/.exec(raw)
  const message = match?.[1]?.trim()

  if (message === undefined || message.length === 0) {
    return 'That did not save. Check your connection and try again.'
  }
  // Convex appends a stack after the message on the same line in some builds.
  return message.split('    at ')[0]?.trim() ?? message
}

/**
 * A mutation whose failures surface instead of vanishing.
 *
 * Same shape as Convex's `useMutation`, so a call site only changes its import.
 *
 * The result is passed through, and is `undefined` when the call failed — the
 * one caller that needs it is Run, which can only learn how many things it
 * actually planned by being told. Everything else keeps ignoring it.
 */
export function useSave<
  Mutation extends FunctionReference<'mutation', 'public'>,
>(
  mutation: Mutation,
): (args: Mutation['_args']) => Promise<Mutation['_returnType'] | undefined> {
  const run: ReactMutation<Mutation> = useMutation(mutation)
  const report = useSaveState((state) => state.report)

  return useCallback(
    async (args: Mutation['_args']) => {
      try {
        return await run(args)
      } catch (error) {
        report(error)
        return undefined
      }
    },
    [run, report],
  )
}
